import React, { useState, useCallback, useRef, useEffect } from 'react';
import LogoHorizontalIcon from '@moda/icons/logo-horizontal-189-x-12';
import HamburgerIcon from '@moda/icons/hamburger-24';
import ExitIcon from '@moda/icons/exit-24';
import FavoriteOutlineIcon from '@moda/icons/favorite-outline-20';
import { colors } from '@moda/tokens';
import { Clickable } from '@moda/om';
import SearchIcon from '@moda/icons/search-20';
import { useHistory, useLocation } from 'react-router-dom';
import { useEnableClubModa } from '../../hooks/useEnableClubModa';
import { QuickSearch } from '../QuickSearch';
import { MiniCart } from '../MiniCart';
import { hrefFor } from '../../routers';
import { MobileNavOverlay } from './MobileNavOverlay';

import './MobileNav.scss';

export const MobileNav: React.FC = () => {
  const enableClubModa = useEnableClubModa();

  const [isOpen, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const history = useHistory();

  const handleFavoriteIconClick = useCallback(() => {
    setOpen(false);

    history.push(hrefFor.FavoritesPage());
  }, [history]);

  const handleOpen = useCallback(() => setOpen(true), []);

  const handleClose = useCallback(() => setOpen(false), []);

  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const handleSearchIconClick = useCallback(() => {
    setShowSearchInput(prevValue => !prevValue);
  }, []);

  const handleLogoClick = useCallback(() => {
    handleClose();
    history.push(hrefFor.HomePage());
  }, [handleClose, history]);

  return (
    <>
      {isOpen && (
        <MobileNavOverlay
          onClose={handleClose}
          navRef={ref.current}
          isSearchOpen={showSearchInput}
        />
      )}

      <nav className="MobileNav" data-testid="mobileNavItems" ref={ref}>
        <div className="MobileNav__contents">
          <div className="MobileNav__rail">
            <div className="MobileNav__actions">
              <Clickable
                aria-label={isOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
                aria-expanded={isOpen}
                className="MobileNav__hamburger"
                onClick={isOpen ? handleClose : handleOpen}
              >
                {!isOpen && <HamburgerIcon />}

                {isOpen && <ExitIcon />}
              </Clickable>

              <SearchIcon className="MobileNav__search-icon" onClick={handleSearchIconClick} />
            </div>

            <Clickable
              aria-label="Moda Operandi Home Page"
              className="MobileNav__logo"
              data-testid="mobileLogo"
              onClick={handleLogoClick}
            >
              <LogoHorizontalIcon
                height="10px"
                color={enableClubModa ? colors.all['waikawa-gray'] : undefined}
              />
            </Clickable>

            <div className="MobileNav__actions">
              <Clickable aria-label="My Favorites" onClick={handleFavoriteIconClick}>
                <FavoriteOutlineIcon className="MobileNav__favorite" />
              </Clickable>

              <MiniCart screen="mobile" />
            </div>
          </div>

          {showSearchInput && (
            <QuickSearch
              className="MobileNav__search"
              placeholder="What are you looking for?"
              mobile
              onSelect={() => setShowSearchInput(false)}
            />
          )}
        </div>
      </nav>
    </>
  );
};
